/**
 * The tenant's vocabulary, as an i18n interpolation context.
 *
 * Grid copy names the nouns it wants — `Static {{relay_load_term_plural}}`,
 * `{{site_term_plural}} Needing Maintenance` — so a label or tooltip reads in
 * the customer's own words without any surface hardcoding them. This assembles
 * every term once and hands the whole set to the translator, rather than each
 * item declaring which single term applies to it: a string that needs two
 * nouns can have both, and copy can start naming a term without the item that
 * carries it being changed to permit it.
 *
 * Adding a term to the platform is therefore a one-line change here plus the
 * field it reads from, and no change at all to the grids that use it.
 *
 * Contains:
 * - useTenantTerms — memoized `{ <term_field>: string }` for the scope
 */

import { useMemo } from 'react';

import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useSiteTerm from '@sophocles/hooks/useSiteTerm';

/**
 * @param {?object} [customer] - resolve against this customer instead of the
 *   ScopeContext read, for flat routes that seed their own (the services
 *   tier). Scope-Layout descendants omit it; a grid listing many customers
 *   omits it too and gets the tenant's own words.
 * @returns {Object<string, string>} Keyed by the term's field name, which is
 *   what the copy interpolates.
 */
export default function useTenantTerms(customer = null) {
  const siteTerm = useSiteTerm(customer);
  const siteGroupTerm = useSiteGroupTerm(customer);
  const relayLoadTerm = useRelayLoadTerm(undefined, customer);

  return useMemo(
    () => ({
      site_term: siteTerm.singular,
      site_term_plural: siteTerm.plural,
      site_group_term: siteGroupTerm.singular,
      site_group_term_plural: siteGroupTerm.plural,
      relay_load_term: relayLoadTerm.singular,
      relay_load_term_plural: relayLoadTerm.plural,
    }),
    [siteTerm, siteGroupTerm, relayLoadTerm],
  );
}
